import { UnfoldMore as UnfoldMoreIcon } from '@mui/icons-material';
import { Grid, Link, Typography } from '@mui/material';
import { useMemo } from 'react';
import { Menu } from 'src/components/menu';
import {
  SORT_DIRECTION,
  SortOptions,
} from 'src/components/subHeader/SubHeaderWithSorting.interface';
import { useMenu } from 'src/hooks';
import { lang } from 'src/utils';

import {
  StyledLocationSortButton,
  TableInfoBottomContainer,
  TableInfoBottomContainerItem,
  TableInfoContainer,
} from '../../CustomTable.styled';
import {
  ControlType,
  RowsPerPageOptions,
  TableControlsHeaderProps,
} from './TableControlsHeader.interface';

export const TableControlsHeader = ({
  page,
  rowsPerPage,
  totalCount,
  selectedSortOption,
  sortItems,
  onSortChange = () => {},
  rowsPerPageItems,
  onRowsPerPageChange = () => {},
  visibleControls = [ControlType.SORT],
  selectedCount = 0,
  onClearSelection = () => {},
}: TableControlsHeaderProps) => {
  const menu = useMenu();
  const rowsPerPageMenu = useMenu();
  const orderVal =
    selectedSortOption?.order === SORT_DIRECTION.DESC
      ? SORT_DIRECTION.ASC
      : SORT_DIRECTION.DESC;

  const start = (page - 1) * rowsPerPage + 1;
  const end = Math.min(page * rowsPerPage, totalCount);

  const defaultSortItems = [
    {
      label: lang.dateCreated,
      isSelected: selectedSortOption?.option === SortOptions.DATE_CREATED,
      onClick: () => {
        menu.onCloseMenu();
        onSortChange({
          option: SortOptions.DATE_CREATED,
          order: orderVal,
        });
      },
    },
    {
      label: lang.aToZ,
      isSelected: selectedSortOption?.option === SortOptions.ALPHABETICALLY,
      onClick: () => {
        menu.onCloseMenu();
        onSortChange({
          option: SortOptions.ALPHABETICALLY,
          order: orderVal,
        });
      },
    },
    {
      label: lang.lastModified,
      isSelected: selectedSortOption?.option === SortOptions.LAST_MODIFIED,
      onClick: () => {
        menu.onCloseMenu();
        onSortChange({
          option: SortOptions.LAST_MODIFIED,
          order: SORT_DIRECTION.DESC,
        });
      },
    },
  ];

  const defaultRowsPerPageItems = useMemo(
    () => [
      {
        label: String(RowsPerPageOptions.TWENTY),
        isSelected: rowsPerPage === RowsPerPageOptions.TWENTY,
        onClick: () => {
          rowsPerPageMenu.onCloseMenu();
          onRowsPerPageChange(RowsPerPageOptions.TWENTY);
        },
      },
      {
        label: String(RowsPerPageOptions.FIFTY),
        isSelected: rowsPerPage === RowsPerPageOptions.FIFTY,
        onClick: () => {
          rowsPerPageMenu.onCloseMenu();
          onRowsPerPageChange(RowsPerPageOptions.FIFTY);
        },
      },
      {
        label: String(RowsPerPageOptions.ONE_HUNDRED),
        isSelected: rowsPerPage === RowsPerPageOptions.ONE_HUNDRED,
        onClick: () => {
          rowsPerPageMenu.onCloseMenu();
          onRowsPerPageChange(RowsPerPageOptions.ONE_HUNDRED);
        },
      },
      {
        label: RowsPerPageOptions.ALL as string,
        isSelected:
          rowsPerPage !== RowsPerPageOptions.TWENTY &&
          rowsPerPage !== RowsPerPageOptions.FIFTY &&
          rowsPerPage !== RowsPerPageOptions.ONE_HUNDRED &&
          rowsPerPage >= totalCount,
        onClick: () => {
          rowsPerPageMenu.onCloseMenu();
          onRowsPerPageChange(totalCount);
        },
      },
    ],
    [rowsPerPage, totalCount, rowsPerPageMenu, onRowsPerPageChange],
  );

  const rowsPerPageLabel = useMemo(() => {
    const selectedLabel = rowsPerPageItems
      ? rowsPerPageItems.find((item) => item.isSelected)?.label
      : defaultRowsPerPageItems.find((item) => item.isSelected)?.label;

    // If no option is selected, show the actual rowsPerPage value instead of defaulting to "All"
    if (!selectedLabel) {
      return String(rowsPerPage);
    }

    return selectedLabel;
  }, [defaultRowsPerPageItems, rowsPerPageItems, rowsPerPage]);

  const isShowingAll = rowsPerPageLabel === RowsPerPageOptions.ALL;

  return (
    <>
      <TableInfoContainer container wrap="nowrap">
        <Grid item>
          <Typography variant="body1" color="grey">
            {totalCount !== 0
              ? isShowingAll
                ? `Showing All`
                : `Showing ${start}–${end} of ${totalCount}`
              : ''}
          </Typography>
        </Grid>

        <Grid>
          <Grid container item wrap="nowrap">
            <Grid
              container
              alignItems="center"
              justifyContent="flex-end"
              wrap="nowrap">
              {visibleControls.includes(ControlType.ITEMS_PER_PAGE) && (
                <Grid container item wrap="nowrap" alignItems="center">
                  <Typography noWrap>{lang.itemsPerPage}</Typography>
                  <StyledLocationSortButton
                    variant="text"
                    endIcon={<UnfoldMoreIcon />}
                    disableRipple
                    onClick={(
                      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
                    ) => rowsPerPageMenu.onOpenMenu(event.currentTarget)}>
                    {rowsPerPageLabel}
                  </StyledLocationSortButton>
                  <Menu
                    anchorEl={rowsPerPageMenu.anchorEl}
                    items={(rowsPerPageItems ?? defaultRowsPerPageItems).map(
                      (item) => ({
                        ...item,
                        onClick: () => {
                          item.onClick?.();
                          rowsPerPageMenu.onCloseMenu();
                        },
                      }),
                    )}
                    onClose={rowsPerPageMenu.onCloseMenu}
                  />
                </Grid>
              )}
              {visibleControls.includes(ControlType.SORT) && (
                <Grid container item wrap="nowrap" alignItems="center">
                  <StyledLocationSortButton
                    variant="text"
                    endIcon={<UnfoldMoreIcon />}
                    disableRipple
                    onClick={(
                      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
                    ) => menu.onOpenMenu(event.currentTarget)}>
                    {lang.sort}
                  </StyledLocationSortButton>
                  <Menu
                    anchorEl={menu.anchorEl}
                    items={(sortItems ?? defaultSortItems).map((item) => ({
                      ...item,
                      onClick: () => {
                        item.onClick?.();
                        menu.onCloseMenu();
                      },
                    }))}
                    onClose={menu.onCloseMenu}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </TableInfoContainer>
      {visibleControls.includes(ControlType.CLEAR_SECTION) &&
        selectedCount > 0 && (
          <TableInfoBottomContainer>
            <TableInfoBottomContainerItem
              container
              spacing={2}
              alignItems="center"
              wrap="nowrap">
              <Grid item>
                <Typography noWrap>
                  {selectedCount}{' '}
                  {selectedCount === 1
                    ? lang.locationGroupIsSelected
                    : lang.locationGroupsAreSelected}
                </Typography>
              </Grid>
              <Grid item>
                <Link
                  component="button"
                  variant="body2"
                  onClick={onClearSelection}>
                  {lang.clearSection}
                </Link>
              </Grid>
            </TableInfoBottomContainerItem>
          </TableInfoBottomContainer>
        )}
    </>
  );
};
